import React from 'react'
import PropTypes from 'prop-types'
import { cx } from '@emotion/css'

import Header from './Header'
import SkillsAndExperienceSection from './SkillsAndExperienceSection'
import LocationAndSalarySection from './LocationAndSalarySection'
import RoleDescriptionSection from './RoleDescriptionSection'
import styles from './styles'

const PositionPreview = ({ className, position, children }) => {
  return (
    <article className={cx(styles.preview, className)}>
      <Header
        positionName={position.name}
        companyImage={position.company?.logoSmallUrl}
        companyBackgroundColor={position.company?.brandColor}
        companyName={position.company?.name}
        companySlug={position.company?.slug}
        isProfilePublished={position.company?.published}
      />
      <SkillsAndExperienceSection
        roleExperience={position.roleExperience}
        otherRoles={position.otherRoles}
        mustHaveSkillExperiences={position.mustHaveSkillExperiences}
        niceToHaveSkills={position.niceToHaveSkills}
        languages={position.languages}
      />
      <LocationAndSalarySection
        workplacePolicy={position.workplacePolicy}
        otherLocations={position.otherLocations}
        requiresWorkAuthorisation={position.requiresWorkAuthorisation}
        workAuthorisation={position.workAuthorisation}
        visaSponsorshipAvailable={position.visaSponsorshipAvailable}
        employmentType={position.employmentType}
        contractDuration={position.contractDuration}
        publishedSalaryRange={position.publishedSalaryRange}
        currency={position.currency}
        remunerationPeriod={position.remunerationPeriod}
      />
      <RoleDescriptionSection description={position.description} />
      {children}
    </article>
  )
}

PositionPreview.propTypes = {
  className: PropTypes.string,
  position: PropTypes.shape({
    name: PropTypes.string,
    company: PropTypes.shape({
      logoSmallUrl: PropTypes.string,
      brandColor: PropTypes.string,
      name: PropTypes.string,
      slug: PropTypes.string,
      published: PropTypes.bool,
    }),
    roleExperience: PropTypes.shape({
      role: PropTypes.shape({
        name: PropTypes.string,
      }),
      yearsExperience: PropTypes.number,
    }),
    otherRoles: PropTypes.arrayOf(PropTypes.shape({ name: PropTypes.string, id: PropTypes.string })),
    mustHaveSkillExperiences: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string,
        skill: PropTypes.shape({ name: PropTypes.string }),
        yearsExperience: PropTypes.number,
      }),
    ),
    niceToHaveSkills: PropTypes.arrayOf(PropTypes.shape({ name: PropTypes.string, id: PropTypes.string })),
    languages: PropTypes.arrayOf(PropTypes.shape({ name: PropTypes.string, iso6391Code: PropTypes.string })),
    workplacePolicy: PropTypes.shape({
      type: PropTypes.oneOf(['HYBRID', 'REMOTE', 'OFFICE']),
      comprehensiveLocations: PropTypes.arrayOf(
        PropTypes.shape({
          displayAddress: PropTypes.string,
        }),
      ),
    }),
    otherLocations: PropTypes.arrayOf(
      PropTypes.shape({
        displayAddress: PropTypes.string,
      }),
    ),
    requiresWorkAuthorisation: PropTypes.bool,
    workAuthorisation: PropTypes.shape({
      countries: PropTypes.arrayOf(
        PropTypes.shape({
          iso2Code: PropTypes.string,
          name: PropTypes.string,
        }),
      ),
      statuses: PropTypes.arrayOf(PropTypes.oneOf(['CITIZEN', 'PERMANENT_RESIDENT', 'WORK_PERMIT'])),
    }),
    visaSponsorshipAvailable: PropTypes.bool,
    employmentType: PropTypes.oneOf(['CONTRACT', 'PERMANENT']),
    contractDuration: PropTypes.number,
    publishedSalaryRange: PropTypes.shape({
      start: PropTypes.number,
      end: PropTypes.number,
    }),
    currency: PropTypes.shape({
      code: PropTypes.string,
    }),
    remunerationPeriod: PropTypes.oneOf(['MONTH', 'YEAR']),
    description: PropTypes.string,
  }),
  children: PropTypes.node,
}

export default PositionPreview
